import React, { useState, useEffect } from "react";
import { FaChevronUp, FaChevronDown } from "react-icons/fa6";
import { formatUtcDate, calcDateDuration } from "../utils";
import { TextTruncate } from "./truncatedText";
import { Switch } from "@headlessui/react";
import { EyeIcon, SearchPlusIcon } from "./deploymentItems";

function DeploymentInfoPanel({
  deployments,
  focusedDeploymentId,
  onSelectedDeploymentChange,
  currentPageProp,
  pageSizeProp,
}: {
  deployments: any[];
  focusedDeploymentId?: string;
  onSelectedDeploymentChange: (deployments: string[]) => void;
  currentPageProp?: number;
  pageSizeProp?: number;
}) {
  const [isListVisible, setListVisible] = useState(true);
  const [searchString, setSearchString] = useState("");
  const [currentPage, setCurrentPage] = useState(currentPageProp || 1);
  const [pageSize, setPageSize] = useState(pageSizeProp || 10);
  const [checkedDeployments, setCheckedDeployments] = useState(
    deployments.map((deployment: any) => deployment.external_id)
  );
  const [isAllChecked, setIsAllChecked] = useState(true);
  const [pulseDeploymentId, setPulseDeploymentId] = useState<string | null>(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  const getDataSetDisplayName = (deployment: any) => {
    return deployment.name.includes(deployment.external_id)
      ? deployment.name
      : `${deployment.name} | ${deployment.external_id}`;
  };

  const filteredDeployments = deployments.filter((deployment: any) => {
    const searchTerm = searchString.trim().toLowerCase();

    const nameIncludes = deployment.name
      ? deployment.name.toLowerCase().includes(searchTerm)
      : false;
    const descriptionIncludes = deployment.description
      ? deployment.description.toLowerCase().includes(searchTerm)
      : false;
    const externalIdIncludes = deployment.external_id
      ? deployment.external_id.toString().toLowerCase().includes(searchTerm)
      : false;
    const combinedIncludes = getDataSetDisplayName(deployment)
      .toLowerCase()
      .includes(searchTerm);
    return (
      nameIncludes ||
      descriptionIncludes ||
      externalIdIncludes ||
      combinedIncludes
    );
  });

  const paginatedFilteredDeployments = filteredDeployments.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const deploymentRefs: Record<string, any> = deployments.reduce((acc: Record<string, any>, deployment: any) => {
    acc[deployment.external_id] = React.createRef();
    return acc;
  }, {});

  useEffect(() => {
    if (focusedDeploymentId) {
      setShouldScroll(true);
    }
  }, [focusedDeploymentId]);

  useEffect(() => {
    if (shouldScroll && focusedDeploymentId && deploymentRefs[focusedDeploymentId]?.current) {
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.position = "fixed";
      document.body.style.overflowY = "scroll";

      // Scroll the deployment into view
      deploymentRefs[focusedDeploymentId].current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.overflowY = "";

      // Start the pulse animation
      setPulseDeploymentId(focusedDeploymentId);

      // Reset the scroll trigger state
      setShouldScroll(false);
    }
  }, [shouldScroll]);

  const handleShowIconChange = (deploymentId: string) => {
    const isAlreadyChecked = checkedDeployments.includes(deploymentId);

    let updatedCheckedDeployments;
    if (isAlreadyChecked) {
      updatedCheckedDeployments = checkedDeployments.filter(
        (id: string) => id !== deploymentId
      );
    } else {
      updatedCheckedDeployments = [...checkedDeployments, deploymentId];
    }

    setCheckedDeployments(updatedCheckedDeployments);

    setIsAllChecked(updatedCheckedDeployments.length === deployments.length);

    onSelectedDeploymentChange(updatedCheckedDeployments);
  };

  const handleAllToggleChange = () => {
    let newCheckedDeployments = [];
    if (!isAllChecked) {
      newCheckedDeployments = deployments.map(
        (deployment: any) => deployment.external_id
      );
    }
    setCheckedDeployments(newCheckedDeployments);
    setIsAllChecked(!isAllChecked);
    onSelectedDeploymentChange(newCheckedDeployments);
  };

  const handleFocusIcon = (deploymentId: string) => {
    setCheckedDeployments([deploymentId]);
    onSelectedDeploymentChange([deploymentId]);
    setIsAllChecked(false);
  };

  return (
    <div className="absolute left-0 top-0 z-10 m-2 grid max-h-[93%] min-w-[25rem] max-w-[25rem] grid-cols-5 gap-4 rounded-md bg-white bg-opacity-75 p-2">
      <div className="col-span-5 mb-2 grid grid-cols-5 items-center gap-4 border-b pb-1">
        <h2 className="col-span-4 pl-2 text-lg font-bold">Deployments</h2>
        <div
          className="origin-center transform cursor-pointer justify-self-end pr-2 transition-transform duration-300"
          onClick={() => setListVisible(!isListVisible)}
          style={{ transformOrigin: "center" }}
        >
          {isListVisible ? (
            <FaChevronUp size={20} data-testid="toggle-icon" />
          ) : (
            <FaChevronDown size={20} data-testid="toggle-icon" />
          )}
        </div>
      </div>
      <div
        className={`duration-400 col-span-5 grid grid-cols-5 gap-4 overflow-y-auto transition-all ease-in-out ${
          isListVisible ? "max-h-[25rem] scroll-smooth" : "max-h-0"
        }`}
        data-testid="deployment-list"
      >
        <div className="col-span-5 flex items-center justify-between p-2">
          <input
            type="text"
            placeholder="Search descriptions..."
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            className="col-span-4 rounded-md border py-2 pr-1"
            style={{ flexBasis: "80%", marginRight: "auto" }}
          />
          <label className="flex items-center">
            <span className="mr-2">All</span>
            <Switch
              checked={isAllChecked}
              onChange={handleAllToggleChange}
              className={`${isAllChecked ? "bg-blue-200" : "bg-gray-200"}
                              relative inline-flex h-6 w-11 items-center rounded-full
                              transition-colors duration-200 ease-in-out`}
            >
              <span
                className={`${isAllChecked ? "translate-x-6" : "translate-x-1"}
                                inline-block h-4 w-4 transform rounded-full bg-white
                                transition-transform duration-200 ease-in-out`}
              />
            </Switch>
          </label>
        </div>
        {paginatedFilteredDeployments.map((deployment: any) => {
          return (
            <div
              key={deployment.external_id}
              ref={deploymentRefs[deployment.external_id]}
              className={`group col-span-5 grid grid-cols-5 gap-4 border-b ${
                deployment.external_id === pulseDeploymentId ? "flash" : ""
              }`}
            >
              <div className="transition-bg col-span-6 grid grid-cols-6 justify-between gap-2 p-2">
                <div className="col-span-5">
                  <h2 className="text-md font-bold">
                    {getDataSetDisplayName(deployment)}
                  </h2>
                  <p>Start: {formatUtcDate(deployment.deployment_start_dt)}</p>
                  <p>End: {formatUtcDate(deployment.deployment_end_dt)}</p>
                  <p className="text-blue-400">
                    Duration:{" "}
                    {calcDateDuration(
                      deployment.deployment_start_dt,
                      deployment.deployment_end_dt
                    )}
                  </p>
                  <TextTruncate text={deployment.description} />
                </div>
                <div className="flex items-start justify-end">
                  <SearchPlusIcon
                    onClick={() => handleFocusIcon(deployment.external_id)}
                  />
                  <EyeIcon
                    isShown={checkedDeployments.includes(
                      deployment.external_id
                    )}
                    onClick={() => handleShowIconChange(deployment.external_id)}
                    deploymentId={deployment.external_id}
                  />
                </div>
              </div>
            </div>
          );
        })}
        <div className="flex justify-between">
          <button
            className="ox-button p-[0.3rem]"
            onClick={() => setCurrentPage((prev: number) => prev - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <button
            className="ox-button p-[0.3rem]"
            onClick={() => setCurrentPage((prev: number) => prev + 1)}
            disabled={currentPage * pageSize >= filteredDeployments.length}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeploymentInfoPanel;
